import { collectAllPages, exists } from "@examspark/common";
import {
  AuthorizationContextInput,
  ModelResourceConnection,
  QuerySearchArgs,
  SearchableResourceConnectionNew,
  SearchableResourceSortableFields,
} from "@examspark/types";

import { GraphQLAPI, fragments } from "..";
import { search } from "../queries/search";
import { listUnitExams as listUnitExamsQuery } from "../queries/unit";

/**
 * Searches for all exams that have a relationship to specified `unitId`.
 *
 * NOTE: This requires ElasticSearch to have these relationships indexed
 * properly. We're no longer searching for `unit-exam`, but rather `exam#v0`
 * records themselves.
 */
export const searchUnitExams = async (
  unitId?: string,
  authorizationContext?: AuthorizationContextInput,
) => {
  if (!unitId) {
    console.warn("Unit ID is not provided. Skipping search.");
  }

  const pages = await collectAllPages(async (nextToken) => {
    const result = await GraphQLAPI.query<{
      search: SearchableResourceConnectionNew;
    }>({
      query: search({
        label: "SearchUnitExams",
        fields: `
         ${fragments.resource.id}
         ${fragments.resource.basic}
         isDeleted
       `,
      }),
      variables: {
        authorizationContext,
        nextToken,
        filter: {
          and: [
            {
              sk: {
                eq: "exam#v0",
              },
            },
            {
              referencesIds: {
                eq: unitId,
              },
            },
          ],
        },
        sort: [
          {
            field: SearchableResourceSortableFields.Name,
          },
        ],
      } satisfies QuerySearchArgs,
    });
    return result.data?.search;
  });

  return pages.flatMap((page) => page.items).filter(exists);
};

// This method uses DDB directly to list exams related to a unit.
export const listUnitExams = async (unitId: string, workspaceId: string) => {
  const pages = await collectAllPages(async (nextToken) => {
    const result = await GraphQLAPI.query<{
      listUnitExams: ModelResourceConnection;
    }>({
      query: listUnitExamsQuery,
      variables: {
        input: {
          id: unitId,
          workspaceId,

          nextToken,
        },
      },
    });

    return result.data?.listUnitExams;
  });

  return (
    pages
      .flatMap((page) => page.items?.map(({ resource }) => resource))
      .filter(exists) ?? []
  );
};
